import { createSlice } from "@reduxjs/toolkit";
import update from "react-addons-update";
import axios from "utils/axios";
import { openSnackbarWithError } from "utils/errors";
import {
  fromBackToFrontPackage,
  fromFrontToBackPackage,
} from "views/packages/utils";
import { dispatch } from "../index";
const initialState = {
  photographPackages: [],
  isLoadingPhotographPackages: false,

  // == creation ==
  isPostingPhotographPackage: false,
  photographPackageCreated: null,

  // == edit ==
  isEditingPhotographPackage: false,
  photographPackageEdited: null,

  // == deletion ==
  isDeletingPhotographPackage: false,
};

const slice = createSlice({
  name: "packages",
  initialState,
  reducers: {
    getPhotographPackages(state) {
      state.isLoading = true;
      state.photographPackages = [];
    },
    getPhotographPackagesSuccess(state, action) {
      state.isLoading = false;
      state.photographPackages = action.payload.map((pp) => ({
        ...pp,
        unit_prices: pp.unit_prices.map((up) => ({
          ...up,
          unit_price: up.unit_price / 100,
        })),
      }));
    },
    getPhotographPackagesFailure(state) {
      state.isLoading = false;
    },

    // == creation ==
    savePhotographPackage(state) {
      state.isPostingPhotographPackage = true;
      state.photographPackageCreated = null;
    },
    savePhotographPackageSuccess(state, action) {
      state.isPostingPhotographPackage = false;
      const sanitizedPackage = fromBackToFrontPackage(action.payload);
      state.photographPackageCreated = { ...sanitizedPackage };
      state.photographPackages = update(state.photographPackages, {
        $splice: [[0, 0, { ...sanitizedPackage }]],
      });
    },
    savePhotographPackageFailure(state, action) {
      state.isPostingPhotographPackage = false;
    },

    // == edit ==
    editPhotographPackage(state) {
      state.isEditingPhotographPackage = true;
      state.photographPackageEdited = null;
    },

    editPhotographPackageSuccess(state, action) {
      state.isEditingPhotographPackage = false;
      const sanitizedPackage = fromBackToFrontPackage(action.payload);
      state.photographPackageEdited = { ...sanitizedPackage };
      state.photographPackages = update(state.photographPackages, {
        $splice: [
          [
            state.photographPackages.findIndex(
              (pp) => pp.id === action.payload.id
            ),
            1,
            { ...sanitizedPackage },
          ],
        ],
      });
    },

    editPhotographPackageFailure(state, action) {
      state.isEditingPhotographPackage = false;
    },
    // == deletion ==
    deletePhotographPackage(state) {
      state.isDeletingPhotographPackage = true;
    },
    deletePhotographPackageSuccess(state, action) {
      state.isDeletingPhotographPackage = false;
      // delete the package from the list
      state.photographPackages = state.photographPackages.filter(
        (photographPackage) => photographPackage.id !== action.payload.id
      );
    },
    deletePhotographPackageFailure(state, action) {
      state.isDeletingPhotographPackage = false;
    },

    // == reset popup ==
    resetPhotographPackageCreatedAndEdited(state) {
      state.photographPackageCreated = null;
      state.photographPackageEdited = null;
    },
  },
});

export function getPhotographPackages() {
  return async () => {
    try {
      dispatch(slice.actions.getPhotographPackages());
      const response = await axios.get("/api/v1/photograph_packages");
      dispatch(slice.actions.getPhotographPackagesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getPhotographPackagesFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function savePhotographPackage(photographPackage) {
  return async () => {
    try {
      dispatch(slice.actions.savePhotographPackage());
      const packageSanitized = fromFrontToBackPackage(photographPackage);
      const response = await axios.post(
        "/api/v1/photograph_packages",
        packageSanitized
      );
      dispatch(slice.actions.savePhotographPackageSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.savePhotographPackageFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function editPhotographPackage(packageId, photographPackage) {
  return async () => {
    try {
      dispatch(slice.actions.editPhotographPackage());
      const response = await axios.put(
        `/api/v1/photograph_packages/${packageId}`,
        fromFrontToBackPackage(photographPackage)
      );
      dispatch(slice.actions.editPhotographPackageSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.editPhotographPackageFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function deletePhotographPackage(photographPackageId) {
  return async () => {
    try {
      dispatch(slice.actions.deletePhotographPackage());
      await axios.delete(`/api/v1/photograph_packages/${photographPackageId}`);
      dispatch(
        slice.actions.deletePhotographPackageSuccess({
          id: photographPackageId,
        })
      );
    } catch (error) {
      dispatch(slice.actions.deletePhotographPackageFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function resetPhotographPackagePopup() {
  return async () => {
    dispatch(slice.actions.resetPhotographPackageCreatedAndEdited());
  };
}

export default slice.reducer;
